<template>
  <div style="height: calc(100vh - 100px)">
    <div class="cluster-map" ref="map"></div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster"; // 플러그인 로드
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import api from '@/api/api';

export default {
  name: "ClusterMap",
  props: {
    center: { type: Array, default: () => [36.5, 127.8] },
    zoom:   { type: Number, default: 6 },
    autoFit: { type: Boolean, default: true },

    /* markercluster 옵션들 */
    maxClusterRadius:        { type: Number, default: 60 }, // 같은 클러스터로 묶는 최대 화면거리(px)
    disableClusteringAtZoom: { type: Number, default: 16 }, // 이 줌 이상이면 개별 표시
    showCoverageOnHover:     { type: Boolean, default: false },
    spiderfyOnMaxZoom:       { type: Boolean, default: true },
  },

  data() {
    return {
      map: null,
      clusterGroup: null,
      locations: [],
    };
  },

  async mounted() {
    await this.fetchQrLocationInfo();
    this.initMap();
    this.renderClusters();
  },

  beforeDestroy() {
    if (this.map) this.map.remove();
  },

  watch: {
    locations: {
      deep: true,
      handler() { this.renderClusters(); }
    }
  },

  methods: {
    async fetchQrLocationInfo() {
      try {
        const urn =
          this.urn ||
          this.$route?.params?.urn ||
          this.$route?.query?.urn;

        if (!urn) {
          console.warn('URN이 없습니다.');
          this.locations = [];
          return;
        }

        const res = await api.get(`/productTrace/readMap?urn=${urn}`);
        const result = res.data.result;
        this.locations = Array.isArray(result?.readMapData) ? result.readMapData : [];

      } catch (e) {
        this.toastError?.("좌표 조회 중 오류가 발생했습니다.");
      } 
    },
    initMap() {
      this.map = L.map(this.$refs.map, {
        preferCanvas: true,
        zoomControl: true,
        attributionControl: false,
      }).setView(this.center, this.zoom);

      // 한글 라벨이 잘 보이는 OSM 기본 타일
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        { maxZoom: 19 }
      ).addTo(this.map);
    },

    renderClusters() {
      if (!this.map) return;

      if (this.clusterGroup) this.map.removeLayer(this.clusterGroup);

      const iconCreateFunction = (cluster) => {
        const count = cluster.getChildCount();
        const t = this.normalizeCount(count, cluster);
        const hue = 120 * (1 - t);
        const light = 40 + 15 * (1 - t);
        const border = `hsl(${hue}, 85%, ${light}%)`;
        const fill   = `hsla(${hue}, 85%, ${light}%, .15)`;
        const size   = Math.max(34, Math.sqrt(count) * 10);
        return L.divIcon({
          html: `<div style="
            width:${size}px;height:${size}px;border-radius:50%;
            border:3px solid ${border}; background:${fill};
            display:flex;align-items:center;justify-content:center;
            font-weight:700;color:#111;box-shadow:0 2px 6px rgba(0,0,0,.2);
          "><span>${count}</span></div>`,
          className: "cluster-icon",
          iconSize: L.point(size, size),
        });
      };

      this.clusterGroup = L.markerClusterGroup({
        showCoverageOnHover: this.showCoverageOnHover,
        spiderfyOnMaxZoom: this.spiderfyOnMaxZoom,
        disableClusteringAtZoom: this.disableClusteringAtZoom,
        maxClusterRadius: this.maxClusterRadius,
        iconCreateFunction,
      });

      const dotHtml = `<div style="
        width:8px;height:8px;border-radius:50%;
        background:#2563eb;border:1px solid #fff;
        box-shadow:0 1px 2px rgba(0,0,0,.2);
      "></div>`;

      const markers = [];
      this.locations.forEach(p => {
        if (typeof p.lat !== 'number' || typeof p.lng !== 'number') return;

        const m = L.marker([p.lat, p.lng], {
          icon: L.divIcon({ html: dotHtml, className: "", iconSize: [8,8] })
        });

        // ✅ 개별 마커 팝업(라벨) 바인딩
        m.bindPopup(this.buildPopup(p), {
          className: 'qr-popup',
          closeButton: false,
          autoPan: true,
          offset: [0, -2]
        });

        // 클릭 시 열기 (spiderfy된 뒤에도 동작)
        m.on('click', () => m.openPopup());

        this.clusterGroup.addLayer(m);
        markers.push([p.lat, p.lng]);
      });

      this.map.addLayer(this.clusterGroup);

      if (this.autoFit && markers.length) {
        this.map.fitBounds(L.latLngBounds(markers), { padding: [20,20] });
      }
    },

    // 현재 지도 내 클러스터 중 가장 작은/큰 카운트 기준으로 0~1 정규화
    normalizeCount(count, cluster) {
      // 이 클러스터가 속한 그룹 내 모든 child 수집(간편 추정)
      const top = cluster._group; // markercluster 내부 참조
      if (!top) return 1;

      let min = Infinity, max = -Infinity;
      top._featureGroup.eachLayer(c => {
        if (c.getChildCount) {
          const n = c.getChildCount();
          if (n < min) min = n;
          if (n > max) max = n;
        }
      });
      if (!isFinite(min) || !isFinite(max) || min === max) return 1;
      return (count - min) / (max - min);
    },

    fmtKST(ts) {
      if (!ts) return '-';
      try {
        return new Date(ts).toLocaleString('ko-KR', {
          timeZone: 'Asia/Seoul',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
      } catch { return ts; }
    },

    buildPopup(p) {
      return `
        <div class="qr-pop p-1">
          <div><span class="k">ID</span><span class="v">${p.id}</span></div>
          <div class="mt-1"><span class="k">TIME</span><span class="v">${this.fmtKST(p.createdAt)}</span></div>
        </div>
      `;
    },
  },
};
</script>

<style scoped>
.cluster-map{ width:100%; height:100%; min-height:380px; }
/* spiderfy로 터졌을 때 라인/아이콘이 너무 두꺼우면 여기서 조절 가능 */
.cluster-map ::v-deep(.leaflet-spider-leg) { stroke-width: 1.2px; }

/* 팝업 컨텐츠 미니 라벨 스타일 */
::v-deep .qr-popup .leaflet-popup-content {
  margin: 6px 8px;
  font-size: 12px;
  line-height: 1.25;
}
::v-deep .qr-pop .k { display:inline-block; min-width: 36px; color:#6b7280; font-weight:600; margin-right:6px; }
::v-deep .qr-popup .leaflet-popup-tip { opacity: .9; }
</style>
